import { useEffect, useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import { Web3Provider } from '@ethersproject/providers';
import { ethers, Contract } from 'ethers';
import { AnimatePresence, motion } from 'framer-motion';
import DJackLobbyJoin from './DJackLobbyJoin';
import DJackLobbyPagination from './DJackLobbyPagination';
import managerABI from '../../contracts/djackGame/ABI';

interface IGameInfoArray {
  address: string;
  players: {
    player1: string;
    player2: string;
    player3: string;
    player4: string;
    player5: string;
  };
  entryFee: string;
}

interface IProps {
  currentPage: React.MutableRefObject<number>;
  allowPageChange: React.MutableRefObject<boolean>;
  forcePageRefresh: React.MutableRefObject<boolean>;
  createGameBlock: React.MutableRefObject<number>;
}

const DJackLobbyJoinMenu: React.FC<IProps> = ({
  currentPage,
  allowPageChange,
  forcePageRefresh,
  createGameBlock
}: IProps) => {
  // states and contexts
  const { library } = useWeb3React<Web3Provider>();
  const [gameInfoArray, setGameInfoArray] = useState<IGameInfoArray[]>([]);

  // fetching functions
  const provider = new ethers.providers.StaticJsonRpcProvider(
    'https://data-seed-prebsc-1-s2.binance.org:8545/'
  );

  const managerAddress = process.env.REACT_APP_DJACKGAME_MANAGER_ADDRESS;
  const contract = new Contract(
    `${managerAddress}`,
    managerABI.ABI,
    library?.getSigner() ? library?.getSigner() : provider
  );

  const getSingleGameFromContract = async (gameAddress: string) => {
    try {
      const info = await contract.getGameInfo(gameAddress);
      return {
        address: info[0].toString(),
        players: {
          player1: info[1][0] ? info[1][0] : undefined,
          player2: info[1][1] ? info[1][1] : undefined,
          player3: info[1][2] ? info[1][2] : undefined,
          player4: info[1][3] ? info[1][3] : undefined,
          player5: info[1][4] ? info[1][4] : undefined
        },
        entryFee: info[5].toString()
      };
    } catch (error) {
      console.error(error);
    }
    return null;
  };

  const populateGameInfo = async (addresses: string[], page: number) => {
    const extendedGameInfo: IGameInfoArray[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const gameAddress of addresses) {
      // eslint-disable-next-line no-await-in-loop
      const info = await getSingleGameFromContract(gameAddress);
      if (info && info.address !== '0') {
        extendedGameInfo.push(info);
      }
    }
    if (page === currentPage.current) {
      allowPageChange.current = true;
      setGameInfoArray(extendedGameInfo);
    } else {
      setGameInfoArray([]);
    }
  };

  const getGameAdresses = async (page: number) => {
    try {
      const fetchedGameAdresses = await contract.getGames(
        8,
        (currentPage.current - 1) * 8 + 2
      );
      await populateGameInfo(
        fetchedGameAdresses.filter(
          (value: string) => value !== ethers.constants.AddressZero
        ),
        page
      );
    } catch (error) {
      console.error(error);
    }
  };

  // cleanup
  const cleanup = () => {
    setGameInfoArray([]);
  };

  useEffect(() => {
    getGameAdresses(currentPage.current);
    return cleanup;
  }, [currentPage.current]);

  // pagination
  const onPageChange = (newPage: number) => {
    if (allowPageChange.current) {
      setGameInfoArray([]);
      allowPageChange.current = false;
      currentPage.current = newPage;
    }
  };

  // listen block changes for force refresh
  useEffect(() => {
    provider.on('block', (blockNumber) => {
      if (
        forcePageRefresh.current &&
        blockNumber > createGameBlock.current + 5
      ) {
        setGameInfoArray([]);
        getGameAdresses(currentPage.current);
        forcePageRefresh.current = false;
      }
    });
    return () => {
      provider.off('block', (blockNumber) => {
        if (
          forcePageRefresh.current &&
          blockNumber === createGameBlock.current + 5
        ) {
          setGameInfoArray([]);
          getGameAdresses(currentPage.current);
          forcePageRefresh.current = false;
        }
      });
      cleanup();
    };
  }, []);

  return (
    <motion.div
      key="djackLobbyJoinCard"
      initial="collapsed"
      animate={gameInfoArray.length > 0 ? 'open' : 'collapsed'}
      exit="collapsed"
      variants={{
        open: {
          height: `${78 + gameInfoArray.length * 69}px`
        },
        collapsed: {
          height: currentPage.current === 1 ? '165px' : '203px'
        }
      }}
      transition={{ duration: 0.6, ease: 'easeInOut' }}
    >
      <motion.div
        key="djackLobbyJoinInnerCard"
        initial="collapsed"
        animate={gameInfoArray.length > 0 ? 'open' : 'collapsed'}
        exit="collapsed"
        variants={{
          open: {
            height: `${63 + gameInfoArray.length * 69}px`
          },
          collapsed: {
            height: currentPage.current === 1 ? '150px' : '188px'
          }
        }}
        transition={{ duration: 0.6, ease: 'easeInOut' }}
        className="innerCard"
      >
        <div className="innerCardTitle">Join a Lobby</div>
        <AnimatePresence exitBeforeEnter>
          {gameInfoArray.length > 0 ? (
            <motion.div
              key="djackLobbyJoinList"
              variants={{
                collapsed: { opacity: 0 },
                open: {
                  opacity: 1,
                  transition: {
                    delayChildren: 0.2,
                    staggerChildren: 0.2
                  }
                }
              }}
              initial="collapsed"
              animate="open"
              exit="collapsed"
            >
              {gameInfoArray.map((c) => (
                <DJackLobbyJoin
                  key={c.address}
                  gameData={c}
                  contract={contract}
                />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="djackLobbyJoinListError"
              variants={{
                collapsed: { opacity: 0 },
                open: {
                  opacity: 1
                }
              }}
              initial="collapsed"
              animate="open"
              exit="collapsed"
            >
              <div className="djackLobbyJoinInfoLoader">
                <div className="spinner">
                  <div className="spinner-container">
                    <svg viewBox="25 25 50 50">
                      <circle
                        cx="50"
                        cy="50"
                        r="20"
                        fill="none"
                        strokeWidth="4"
                        strokeMiterlimit="10"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {(gameInfoArray.length > 0 || currentPage.current !== 1) && (
          <DJackLobbyPagination
            currentPage={currentPage.current}
            isLastPage={gameInfoArray.length < 8 || !allowPageChange.current}
            onPageChange={onPageChange}
          />
        )}
      </motion.div>
    </motion.div>
  );
};

export default DJackLobbyJoinMenu;
